// react
import { useContext, useMemo, useState } from 'react';

// context
import { ContextBanner } from '../../../App.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// types
import { PlanningActivity } from '../../../@types';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// mui
import { alpha, Avatar, Box, Checkbox, Chip, Typography } from '@mui/material';

// icons
import { AccountTree as IconActivity } from '@mui/icons-material';

// data
import { COLORS } from '../../../data';

// utils
import { formatCurrency } from '../../../utils/currency';

// Props

export interface DialogAssignActivitiesProps {
	activities: PlanningActivity[];
	assignField: 'componentId' | 'categoryId' | 'expenseTypeId' | 'workPlan';
	assignToId: number;
	assignToName: string;
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

// Component

export const DialogAssignActivities = ({
	activities,
	assignField,
	assignToId,
	assignToName,
	isOpen,
	onClose,
	onSuccess,
}: DialogAssignActivitiesProps) => {
	const { updateActivity } = useContext(ContextSpendPlan);
	const { setBanner } = useContext(ContextBanner);
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [isLoading, setIsLoading] = useState(false);

	// Filter to unassigned activities
	const unassigned = useMemo(() => {
		const fieldMap: Record<string, keyof PlanningActivity> = {
			componentId: 'component',
			categoryId: 'category',
			expenseTypeId: 'expenseType',
			workPlan: 'workPlan',
		};
		const field = fieldMap[assignField];
		return activities.filter((a) => !a[field]);
	}, [activities, assignField]);

	const handleToggle = (id: number) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handleToggleAll = () => {
		if (selected.size === unassigned.length) {
			setSelected(new Set());
		} else {
			setSelected(new Set(unassigned.map((a) => a.id)));
		}
	};

	const handleAssign = async () => {
		setIsLoading(true);
		try {
			const promises = Array.from(selected).map((activityId) =>
				updateActivity(activityId, { [assignField]: assignToId })
			);
			const results = await Promise.all(promises);
			const failures = results.filter((r) => r.error);
			if (failures.length === 0) {
				setBanner({
					message: `${selected.size} ${selected.size === 1 ? 'activity' : 'activities'} assigned to ${assignToName}`,
					severity: 'success',
				});
				setSelected(new Set());
				onSuccess();
			} else {
				setBanner({
					message: `${failures.length} of ${selected.size} assignments failed`,
					severity: 'error',
				});
			}
		} catch {
			setBanner({ message: 'Failed to assign activities', severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const label =
		assignField === 'componentId'
			? 'component'
			: assignField === 'categoryId'
				? 'category'
				: assignField === 'expenseTypeId'
					? 'expense type'
					: 'work plan';

	return (
		<DialogActions
			actions={[
				{ children: 'Cancel', onClick: onClose },
				{
					children: `Assign ${selected.size} ${selected.size === 1 ? 'Activity' : 'Activities'}`,
					disabled: selected.size === 0,
					loading: isLoading,
					onClick: handleAssign,
					variant: 'contained',
				},
			]}
			hasClose
			isOpen={isOpen}
			maxWidth={640}
			onClose={onClose}
			title={{
				children: `Assign Activities to ${assignToName}`,
				variant: 'h3',
			}}>
			{unassigned.length === 0 ? (
				<Box sx={{ py: 4, textAlign: 'center' }}>
					<Typography color="text.secondary">
						All activities already have a {label} assigned.
					</Typography>
				</Box>
			) : (
				<Box>
					<Typography sx={{ fontSize: 13, color: '#6b7280', mb: 2 }}>
						Select activities without a {label} to assign to <strong>{assignToName}</strong>.
					</Typography>

					{/* Select all */}
					<Box
						onClick={handleToggleAll}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							px: 1,
							py: 0.75,
							cursor: 'pointer',
							borderBottom: '1px solid',
							borderColor: 'divider',
							'&:hover': { bgcolor: '#f9fafb' },
						}}>
						<Checkbox
							checked={selected.size === unassigned.length}
							indeterminate={selected.size > 0 && selected.size < unassigned.length}
							size="small"
						/>
						<Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
							Select All ({unassigned.length})
						</Typography>
					</Box>

					{/* Activity list */}
					<Box sx={{ maxHeight: 400, overflow: 'auto' }}>
						{unassigned.map((activity) => {
							const workPlanName =
								activity.workPlan && typeof activity.workPlan === 'object'
									? activity.workPlan.name
									: null;
							return (
								<Box
									key={activity.id}
									onClick={() => handleToggle(activity.id)}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1.5,
										px: 1,
										py: 1,
										cursor: 'pointer',
										borderBottom: '1px solid',
										borderColor: '#f3f4f6',
										transition: 'background-color 0.15s',
										bgcolor: selected.has(activity.id)
											? alpha(COLORS.primary.main, 0.04)
											: 'transparent',
										'&:hover': { bgcolor: alpha(COLORS.primary.main, 0.06) },
										'&:last-child': { borderBottom: 'none' },
									}}>
									<Checkbox checked={selected.has(activity.id)} size="small" />
									<Avatar
										sx={{
											bgcolor: alpha(COLORS.primary.main, 0.08),
											height: 32,
											width: 32,
										}}>
										<IconActivity sx={{ color: COLORS.primary.main, fontSize: 16 }} />
									</Avatar>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }} noWrap>
											{activity.name}
										</Typography>
										{workPlanName && (
											<Typography sx={{ fontSize: 12, color: '#6b7280' }} noWrap>
												{workPlanName}
											</Typography>
										)}
									</Box>
									<Typography
										sx={{
											fontSize: 13,
											fontWeight: 600,
											fontFeatureSettings: '"tnum"',
											color: '#374151',
											flexShrink: 0,
										}}>
										{formatCurrency({
											value: activity.amount,
											currency: activity.currency,
											decimals: 0,
										})}
									</Typography>
									<Chip
										label={activity.status.replace(/_/g, ' ')}
										size="small"
										sx={{
											fontWeight: 600,
											fontSize: 11,
											height: 22,
											flexShrink: 0,
										}}
									/>
								</Box>
							);
						})}
					</Box>
				</Box>
			)}
		</DialogActions>
	);
};
